import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { AuditTrailViewer } from '../../components/AuditTrail';
import DeleteRecordButton, { deleteNeedsReason } from '../../components/DeleteRecordButton';
import { useAlert } from '../../context/AlertContext';
import { usePermissions } from '../../context/PermissionContext';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';

const AuditDetail = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { auditId } = useParams();
  const { showSuccess, showError, confirm } = useAlert();
  const { hasPermission } = usePermissions();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details' or 'audit'
  // S2-26: "Send back" (reject) dialog — a reason is mandatory.
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    const loadAudit = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/audit-records/${auditId}`, { headers });
        setAudit(res.data);
      } catch (err) {
        console.error('Failed to fetch audit:', err);
        showError('Failed to load audit details');
      } finally {
        setLoading(false);
      }
    };
    loadAudit();
  }, [auditId]);

  const fetchAudit = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/audit-records/${auditId}`, { headers });
      setAudit(res.data);
    } catch (err) {
      console.error('Failed to fetch audit:', err);
      showError('Failed to load audit details');
    } finally {
      setLoading(false);
    }
  };

  const handleWorkflowAction = async (action) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/audit-records/${auditId}/${action}`, {}, { headers });
      showSuccess(`Audit ${action} successfully!`);
      fetchAudit(); // Refresh data
    } catch (err) {
      console.error(`Failed to ${action} audit:`, err);
      showError(err.response?.data?.detail || `Failed to ${action} audit`);
    } finally {
      setActionLoading(false);
    }
  };

  // S2-26: send a Submitted / Reviewed audit back to Draft with a reason.
  const handleReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/audit-records/${auditId}/reject`, { reason }, { headers });
      showSuccess('Audit sent back to Draft');
      setShowRejectDialog(false);
      setRejectReason('');
      fetchAudit();
    } catch (err) {
      console.error('Failed to send audit back:', err);
      const detail = err.response?.data?.detail;
      showError(typeof detail === 'string' ? detail : 'Failed to send audit back');
    } finally {
      setActionLoading(false);
    }
  };

  const createCAPAFromFinding = async (finding) => {
    const confirmed = await confirm(
      'Create CAPA',
      `Create CAPA for: "${finding.question}"?`,
      { confirmLabel: 'Create', destructive: false }
    );
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      
      const capaData = {
        title: `${audit.audit_title} - ${finding.question}`,
        description: finding.observations || `Finding from audit: ${finding.question}`,
        capa_type: 'Corrective',
        severity: finding.severity || 'Medium',
        priority: 'Medium',
        source: 'Audit',
        source_id: auditId,
        source_title: audit.audit_title,
        root_cause: finding.observations || '',
        corrective_action: finding.recommended_action || '',
        preventive_action: '',
        assigned_to: audit.lead_auditor_id,
        assigned_to_name: audit.lead_auditor_name,
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      };

      const res = await axios.post(`${API}/capa`, capaData, { headers });
      
      // Update finding with CAPA ID
      const updatedFindings = audit.findings.map(f =>
        f.id === finding.id ? { ...f, capa_id: res.data.id } : f
      );
      await axios.put(`${API}/audit-records/${auditId}`, { findings: updatedFindings }, { headers });

      showSuccess('CAPA created successfully!');
      fetchAudit();
    } catch (err) {
      console.error('Failed to create CAPA:', err);
      showError('Failed to create CAPA');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-700',
      'Submitted': 'bg-blue-100 text-blue-700',
      'Reviewed': 'bg-purple-100 text-purple-700',
      'Approved': 'bg-green-100 text-green-700',
      'CAPA Outstanding': 'bg-yellow-100 text-yellow-700',
      'Closed': 'bg-teal-100 text-teal-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'Critical': 'bg-red-100 text-red-700',
      'Major': 'bg-orange-100 text-orange-700',
      'Moderate': 'bg-yellow-100 text-yellow-700',
      'Minor': 'bg-blue-100 text-blue-700'
    };
    return colors[severity] || 'bg-gray-100 text-gray-700';
  };

  // Render an audit team list (co-auditors / auditees) as name pills. External
  // members have no platform login, so they're marked and coloured distinctly.
  const renderTeamPills = (members) => {
    if (!members || members.length === 0) {
      return <p className="font-semibold text-gray-500">N/A</p>;
    }
    return (
      <div className="flex flex-wrap gap-2">
        {members.map((member, index) => {
          const isExternal = member.is_external || !member.user_id;
          return (
            <span
              key={member.id || member.user_id || `${member.name}-${index}`}
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                isExternal ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
              }`}
            >
              {member.name}
              {isExternal && (
                <span className="text-xs opacity-80">
                  {member.organization ? `(External · ${member.organization})` : '(External)'}
                </span>
              )}
            </span>
          );
        })}
      </div>
    );
  };

  const canPerformAction = (action) => {
    if (!user) return false;

    switch (action) {
      case 'submit': {
        const allCompleted = audit.findings?.length > 0 && audit.findings.every(f => f.status);
        return audit.workflow_status === 'Draft' && hasPermission('audits', 'update') && allCompleted;
      }
      case 'review':
        return audit.workflow_status === 'Submitted' && hasPermission('audits', 'approve');
      case 'approve':
        return audit.workflow_status === 'Reviewed' && hasPermission('audits', 'approve');
      case 'close':
        return (audit.workflow_status === 'CAPA Outstanding' || audit.workflow_status === 'Approved') && hasPermission('audits', 'approve');
      case 'reject':
        return (audit.workflow_status === 'Submitted' || audit.workflow_status === 'Reviewed') && hasPermission('audits', 'approve');
      default:
        return false;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="p-6">
          <p className="text-red-600">Audit not found</p>
        </div>
      </div>
      </div>
    );
  }

  const failedFindings = audit.findings?.filter(f => f.status === 'Fail') || [];

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/audits/records')}
            className="text-blue-600 hover:underline mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Audit Records
          </button>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">{audit.audit_title}</h1>
                <p className="text-gray-600 mt-1">{audit.audit_type}</p>
              </div>
              <div className="flex items-center gap-3">
                {audit.workflow_status === 'Draft' && (
                  <button
                    onClick={() => navigate('/audits/conduct', { state: { auditRecordId: audit.id } })}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-semibold"
                  >
                    Continue Audit
                  </button>
                )}
                <span className={`px-4 py-2 rounded-full text-sm font-bold ${getStatusColor(audit.workflow_status)}`}>
                  {audit.workflow_status}
                </span>
                {/* Soft delete — any status (reason required unless the workflow
                    is Draft); backend enforces audits:delete + site access + the
                    reason rule. */}
                {hasPermission('audits', 'delete') && (
                  <DeleteRecordButton
                    url={`${API}/audit-records/${audit.id}`}
                    label={`audit "${audit.audit_title}"`}
                    requireReason={deleteNeedsReason(audit.workflow_status || 'Draft')}
                    onDeleted={() => navigate('/audits/records')}
                  />
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Audit Date</p>
                <p className="font-semibold">{formatDate(audit.audit_date)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Lead Auditor</p>
                <p className="font-semibold">{audit.lead_auditor_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Location</p>
                <p className="font-semibold">{audit.location || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Department</p>
                <p className="font-semibold">{audit.department || 'N/A'}</p>
              </div>
            </div>

            {/* Audit team — full-width rows since these lists can be long */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
              <div>
                <p className="text-sm text-gray-600 mb-1">Co-Auditors / Joint Auditors</p>
                {renderTeamPills(audit.co_auditors)}
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Auditees (Participating Members)</p>
                {renderTeamPills(audit.auditees)}
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('details')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'audit'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Audit History
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' ? (
          <>
        {/* Metrics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-green-50 rounded-xl shadow-md p-4 border-l-4 border-green-500">
            <p className="text-sm text-green-600 font-semibold">Compliance Score</p>
            <p className="text-3xl font-bold text-green-700">{audit.compliance_score?.toFixed(1)}%</p>
          </div>
          <div className="bg-blue-50 rounded-xl shadow-md p-4 border-l-4 border-blue-500">
            <p className="text-sm text-blue-600 font-semibold">Total Checks</p>
            <p className="text-3xl font-bold text-blue-700">{audit.total_checks}</p>
          </div>
          <div className="bg-yellow-50 rounded-xl shadow-md p-4 border-l-4 border-yellow-500">
            <p className="text-sm text-yellow-600 font-semibold">Failed</p>
            <p className="text-3xl font-bold text-yellow-700">{audit.failed_checks}</p>
          </div>
          <div className="bg-red-50 rounded-xl shadow-md p-4 border-l-4 border-red-500">
            <p className="text-sm text-red-600 font-semibold">Critical</p>
            <p className="text-3xl font-bold text-red-700">{audit.critical_findings}</p>
          </div>
        </div>

        {/* Workflow Actions */}
        {(canPerformAction('submit') || canPerformAction('review') || canPerformAction('approve') || canPerformAction('close') || canPerformAction('reject')) && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Workflow Actions</h2>
            <div className="flex gap-3">
              {audit.workflow_status === 'Draft' && hasPermission('audits', 'update') && !canPerformAction('submit') && (
                <p className="text-sm text-yellow-700 bg-yellow-50 px-4 py-2 rounded-lg">
                  All checklist items must be completed before submitting for review.
                </p>
              )}
              {canPerformAction('submit') && (
                <button
                  onClick={() => handleWorkflowAction('submit')}
                  disabled={actionLoading}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  Submit for Review
                </button>
              )}
              {canPerformAction('review') && (
                <button
                  onClick={() => handleWorkflowAction('review')}
                  disabled={actionLoading}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300"
                >
                  Mark as Reviewed
                </button>
              )}
              {canPerformAction('approve') && (
                <button
                  onClick={() => handleWorkflowAction('approve')}
                  disabled={actionLoading}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300"
                >
                  Approve Audit
                </button>
              )}
              {canPerformAction('reject') && (
                <button
                  onClick={() => setShowRejectDialog(true)}
                  disabled={actionLoading}
                  className="px-6 py-3 bg-white text-red-700 border border-red-300 rounded-lg hover:bg-red-50 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  Send Back to Draft
                </button>
              )}
              {canPerformAction('close') && (
                <button
                  onClick={() => handleWorkflowAction('close')}
                  disabled={actionLoading}
                  className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-300"
                >
                  Close Audit
                </button>
              )}
            </div>
          </div>
        )}

        {/* S2-26: Send back to Draft — reason is mandatory and audit-logged. */}
        <Dialog
          open={showRejectDialog}
          onOpenChange={(open) => {
            if (actionLoading) return;
            setShowRejectDialog(open);
            if (!open) setRejectReason('');
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Send audit back to Draft</DialogTitle>
              <DialogDescription>
                The auditor will need to rework and resubmit it. Your reason is recorded in the audit history.
              </DialogDescription>
            </DialogHeader>
            <label htmlFor="audit-reject-reason" className="block text-sm font-medium text-gray-700">
              Reason <span className="text-red-600">*</span>
            </label>
            <textarea
              id="audit-reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              maxLength={2000}
              className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
              placeholder="What needs to be corrected?"
            />
            <DialogFooter>
              <button
                type="button"
                onClick={() => { setShowRejectDialog(false); setRejectReason(''); }}
                disabled={actionLoading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={actionLoading || !rejectReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300"
              >
                Send Back
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Failed Findings / Non-Conformances */}
        {failedFindings.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">🚨 Non-Conformances ({failedFindings.length})</h2>
            <div className="space-y-4">
              {failedFindings.map((finding, index) => (
                <div key={finding.id} className="border-2 border-red-200 rounded-lg p-4 bg-red-50">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-800">{index + 1}. {finding.question}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getSeverityColor(finding.severity)}`}>
                      {finding.severity}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">Category: {finding.category}</p>
                  {finding.observations && (
                    <div className="mb-2">
                      <p className="text-sm font-semibold text-gray-700">Observations:</p>
                      <p className="text-sm text-gray-600">{finding.observations}</p>
                    </div>
                  )}
                  {finding.recommended_action && (
                    <div className="mb-3">
                      <p className="text-sm font-semibold text-gray-700">Recommended Action:</p>
                      <p className="text-sm text-gray-600">{finding.recommended_action}</p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    {finding.capa_id ? (
                      <button
                        onClick={() => navigate(`/capa/${finding.capa_id}`)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                      >
                        View CAPA
                      </button>
                    ) : (
                      hasPermission('capa', 'create') && (
                        <button
                          onClick={() => createCAPAFromFinding(finding)}
                          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm"
                        >
                          Create CAPA
                        </button>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Findings */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">📋 All Checklist Items ({audit.findings?.length || 0})</h2>
          <div className="space-y-3">
            {audit.findings?.map((finding, index) => (
              <div
                key={finding.id}
                className={`p-4 rounded-lg border ${
                  finding.status === 'Pass'
                    ? 'bg-green-50 border-green-200'
                    : finding.status === 'Fail'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800">{index + 1}. {finding.question}</p>
                    <p className="text-xs text-gray-600 mt-1">{finding.category}</p>
                    {finding.observations && (
                      <p className="text-sm text-gray-600 mt-2">{finding.observations}</p>
                    )}
                  </div>
                  <div className="flex gap-2 items-center">
                    {finding.severity && (
                      <span className={`px-2 py-1 rounded text-xs font-bold ${getSeverityColor(finding.severity)}`}>
                        {finding.severity}
                      </span>
                    )}
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      finding.status === 'Pass'
                        ? 'bg-green-100 text-green-700'
                        : finding.status === 'Fail'
                        ? 'bg-red-100 text-red-700'
                        : finding.status === 'N/A'
                        ? 'bg-gray-100 text-gray-700'
                        : 'bg-yellow-50 text-yellow-600'
                    }`}>
                      {finding.status || 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Notes */}
        {audit.summary_notes && (
          <div className="bg-white rounded-xl shadow-md p-6 mt-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">📝 Summary Notes</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{audit.summary_notes}</p>
          </div>
        )}

        {/* Approval Trail */}
        {(audit.reviewer_name || audit.approver_name) && (
          <div className="bg-white rounded-xl shadow-md p-6 mt-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">✅ Approval Trail</h2>
            <div className="space-y-2">
              {audit.reviewer_name && (
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded text-sm font-semibold">Reviewed</span>
                  <span className="text-gray-700">by {audit.reviewer_name}</span>
                  <span className="text-gray-500 text-sm">{formatDateTime(audit.reviewed_at)}</span>
                </div>
              )}
              {audit.approver_name && (
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm font-semibold">Approved</span>
                  <span className="text-gray-700">by {audit.approver_name}</span>
                  <span className="text-gray-500 text-sm">{formatDateTime(audit.approved_at)}</span>
                </div>
              )}
            </div>
          </div>
        )}
          </>
        ) : (
          /* Audit History Tab */
          <div className="bg-white rounded-xl shadow-md p-6">
            <AuditTrailViewer
              entityType="audit_record"
              entityId={auditId}
              title="Audit Record History"
            />
          </div>
        )}
      </div>
    </div>
      </div>
  );
};

export default AuditDetail;
